import { ReactNode } from "react";

import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
  VisuallyHidden
} from "@chakra-ui/react";

import { BsInfoCircle } from "react-icons/bs";

import { FCWithControl } from "@/jenesys-types/components";

import { useFieldsetContext } from "@/components/forms/FormFieldset/FormFieldset";
import { useFormError } from "@/hooks/form/useFormError";

export interface FormFieldProps {
  name: string;
  label: string;
  hideLabel?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  helpText?: ReactNode;
  useTooltip?: boolean;
  sideBySide?: boolean;
}

export const FormField: FCWithControl<FormFieldProps> = (props) => {
  const fieldset = useFieldsetContext();
  const {
    name,
    label,
    isDisabled = fieldset?.isDisabled ?? false,
    isLoading = false,
    isRequired = false,
    isReadOnly = false,
    hideLabel = false,
    helpText,
    type,
    useTooltip = false,
    sideBySide = false,
    control: Control,
    ...controlProps
  } = props;

  const { isInvalid, message } = useFormError(name);

  const formLabel = <FormLabel fontSize="sm">{label}</FormLabel>;

  return (
    <FormControl
      id={name}
      isInvalid={!!isInvalid}
      isDisabled={isDisabled}
      isRequired={isRequired}
      isReadOnly={isReadOnly}
      display={type === "hidden" ? "none" : undefined}
      cursor={isReadOnly ? "no-drop" : "initial"}
    >
      <Box>
        <HStack gap={0}>
          {hideLabel ? (
            <VisuallyHidden>
              <Text fontSize="xs">{formLabel}</Text>
            </VisuallyHidden>
          ) : (
            formLabel
          )}
          {helpText && useTooltip && (
            <Popover placement="right" trigger="hover">
              <PopoverTrigger>
                <span>
                  <Icon fontSize="xs" as={BsInfoCircle} mr={2} mb={2} />
                </span>
              </PopoverTrigger>
              <PopoverContent color="black" bg="white">
                <PopoverArrow bg="white" />
                <PopoverBody p={3} fontSize="xs">
                  {helpText}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </HStack>
      </Box>

      <Skeleton
        isLoaded={!isLoading}
        w="100%"
        pointerEvents={isReadOnly ? "none" : "initial"}
      >
        {helpText && !useTooltip && !sideBySide && (
          <FormHelperText mt={0} mb={2}>
            {helpText}
          </FormHelperText>
        )}
        {sideBySide ? (
          <HStack>
            <Control
              name={name}
              isDisabled={isDisabled}
              isRequired={isRequired}
              type={type}
              bg="white"
              {...controlProps}
            />
            {helpText && !useTooltip && (
              <FormHelperText mt={0}>{helpText}</FormHelperText>
            )}
          </HStack>
        ) : (
          <Control
            name={name}
            isDisabled={isDisabled}
            isRequired={isRequired}
            type={type}
            bg="white"
            {...controlProps}
          />
        )}

        {isInvalid && <FormErrorMessage>{message}</FormErrorMessage>}
      </Skeleton>
    </FormControl>
  );
};
